<template>
  <div class="relative rounded-md">
    <PInput
      v-bind="inheritedAttrs()"
      :model-value="validatedInput"
      ref="input"
      draggable="false"
      @focusin="hasFocus = true"
      @focusout="hasFocus = false"
      @paste.prevent="onPaste"
      @input.stop="onInput"
      @dragstart.prevent
      @keydown="onKeyDown"
      :color="errors.length ? 'error' : (attrs.color as PInputProps['color'])"
      :aria-invalid="errors.length ? true : attrs['aria-invalid']"
      class="w-full"
      :class="{ '[&_input]:text-error [&_input]:placeholder:text-error': errors.length }"
    >
      <template v-if="slots.leading || slots.default" #leading="slotProps">
        <slot name="leading" v-bind="slotProps">
          <span class="pointer-events-none"><slot v-bind="slotProps" /></span>
        </slot>
      </template>
      <template v-if="slots.trailing || errors.length" #trailing="slotProps">
        <slot name="trailing" v-bind="slotProps">
          <FontAwesomeIcon
            :icon="faCircleExclamation"
            size="sm"
            aria-hidden="true"
            class="text-error pointer-events-none"
          />
        </slot>
      </template>
    </PInput>
  </div>
  <p
    v-if="errors && errors.length > 0 && errors.some(Boolean) && !hideErrorMessages"
    class="text-error mt-2 text-xs text-nowrap"
  >
    {{ errors.filter(Boolean).join(", ") }}
  </p>
</template>

<script setup lang="ts">
import { computed, ref, useAttrs, useTemplateRef, watch, type Ref } from "vue"
import { normalizeDecimal } from "@/helper/number-helper"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons"
import PInput from "./PInput.vue"
import type { PInputProps, PInputSlots } from "./input"

const props = withDefaults(
  defineProps<{
    modelValue?: string | number
    /** Format external values with Dutch decimal and thousands separators. */
    format?: boolean
    /** Use arrow keys to increment and decrement numeric quantities. */
    incrementOnArrowKeys?: boolean
    errors?: string[]
    hideErrorMessages?: boolean
    maxDecimals?: number
    minDecimals?: number
    maxLeadingDigits?: number
    allowNegative?: boolean
    allowPositive?: boolean
    allowZero?: boolean
    naturalNumbersOnly?: boolean
    decimalPreventedMessage?: string
    localErrorTimeoutMs?: number | null
    transformClipboard?: (value: string) => string
  }>(),
  {
    modelValue: "",
    format: true,
    incrementOnArrowKeys: true,
    errors: () => [],
    hideErrorMessages: false,
    maxDecimals: 8,
    minDecimals: 2,
    maxLeadingDigits: 8,
    allowPositive: true,
    allowNegative: false,
    allowZero: false,
    naturalNumbersOnly: false,
    localErrorTimeoutMs: 2000
  }
)

const emit = defineEmits(["update:modelValue", "input"])

defineOptions({
  inheritAttrs: false
})

const slots = defineSlots<PInputSlots>()

const validatedInput = ref("")

const hasFocus = ref(false)

const attrs = useAttrs()

function inheritedAttrs(): Record<string, unknown> {
  const _inheritedAttrs: Record<string, unknown> = { ...attrs }
  delete _inheritedAttrs["v-model"] // We want all attrs to be inherited, except v-model
  delete _inheritedAttrs["v-on:input"] // We want all attrs to be inherited, except v-model
  return _inheritedAttrs
}

const localErrors: Ref<string[]> = ref([])

const errors = computed(() => {
  return localErrors.value.length ? localErrors.value : props.errors
})

function onPaste(e: ClipboardEvent) {
  // ...default behaviour is prevented by .prevent modifier...

  let pastedText = e.clipboardData?.getData("text") ?? ""

  if (props.transformClipboard) {
    pastedText = props.transformClipboard(pastedText)
  }

  // Simulate input event
  const mockEvent = new Event("input")
  Object.defineProperty(mockEvent, "target", {
    value: { value: pastedText }
  })
  onInput(mockEvent)
}

/**
 * Handle input event, converts Dutch number formatting to numeric value.
 * @param e - The event object.
 */
function onInput(e: Event) {
  const target = e.target as HTMLInputElement
  let val = target.value ?? ""

  // Remove thousands separators from user input before processing, only remove dots that are followed by exactly 3 digits (thousands separators)
  val = val.replace(/\.(?=\d{3}(?!\d))/g, "")

  // Replace comma with period
  val = val.replace(/,/g, ".")

  // Replace any chars that != 0-9, period or dash
  val = val.replace(/[^0-9.-]/g, "")

  /* Handle dashes */
  if (val.includes("-")) {
    if (props.allowNegative) {
      val = val.replace(/(?!^)-/g, "") // Remove all dashes except the first one
    } else {
      val = ""
    }
  }

  /* Handle positive numbers */
  if (!props.allowPositive && !val.startsWith("-") && val !== "") {
    val = ""
  }

  /* Handle non-natural (i.e. decimal) number */
  if (props.naturalNumbersOnly) {
    if (val.includes(".")) {
      // Remove any non-digit characters
      val = val.replace(/[^0-9]/g, "")
      displayDecimalPreventedError()
    } else {
      // Clear error when input is valid
      localErrors.value = []
    }
  }

  /* Handle zero */
  if (!props.allowZero && val === "0") {
    val = ""
  }

  /* Allow only one period */
  const parts = val.split(".")
  if (parts.length > 2) {
    val = `${parts[0]}.${parts.slice(1).join("")}`
  }

  /* Turn . into 0. */
  if (val.startsWith(".")) {
    val = `0${val}`
  }

  /* Restrict number of leading digits and decimals */
  const [integerPart, decimalPart] = val.split(".")
  if (integerPart.length > props.maxDecimals) {
    val = integerPart.substring(0, props.maxDecimals) + (decimalPart ? `.${decimalPart}` : "")
  }
  if (decimalPart && decimalPart.length > 2) {
    val = `${integerPart}.${decimalPart.substring(0, props.maxLeadingDigits)}`
  }

  const visibleValue = val.toString().replace(".", ",")
  validatedInput.value = visibleValue
  target.value = visibleValue // This will be visible to the user

  /* Remove trailing period before emit (otherwise server will interpret as scientific notation). */
  if (val.endsWith(".")) {
    val = val.slice(0, -1)

    /* This might cause value to be zero, which might not be allowed */
    if (!props.allowZero && val === "0") {
      val = ""
    }
  }

  /* Do not emit dash only */
  if (val === "-") {
    val = ""
  }

  /* Do not emit negative zeros only */
  if (val.startsWith("-") && parseFloat(val) === 0) {
    val = "" // or set it to a different value as needed
  }

  /* First emit the update event, and then the input event.
      Otherwise parent will receive input event before the value has been updated. */
  emit("update:modelValue", val)
  emit("input", val)
}

/**
 * Handle arrow up/down keys to increment/decrement the value.
 */
function onKeyDown(event: KeyboardEvent) {
  const key = event.key

  if (props.incrementOnArrowKeys && (key === "ArrowUp" || key === "ArrowDown")) {
    event.preventDefault()

    const currentValue = parseFloat(props.modelValue as string) || 0
    const step = 1
    const newValue = key === "ArrowUp" ? currentValue + step : currentValue - step

    const mockEvent = new Event("input")
    Object.defineProperty(mockEvent, "target", {
      value: { value: newValue.toString() }
    })

    onInput(mockEvent)
  }
}

watch(
  () => props.modelValue,
  (val) => {
    if (!hasFocus.value) {
      // If the input is not focused, normalize the value.
      validatedInput.value = !props.format
        ? String(val ?? "")
        : !props.naturalNumbersOnly
          ? normalizeDecimal(val, { minDecimalPlaces: props.minDecimals, thousandsSeparator: "." })
          : normalizeDecimal(val, { minDecimalPlaces: 0, thousandsSeparator: "." })
    }
  },
  { immediate: true }
)

const input = useTemplateRef<InstanceType<typeof PInput>>("input")

function focus() {
  input.value!.focus()
}
function select() {
  input.value!.select()
}
defineExpose({ focus, select })

let localErrorTimeout: ReturnType<typeof setTimeout> | undefined
function displayDecimalPreventedError() {
  if (props.decimalPreventedMessage) {
    clearTimeout(localErrorTimeout)
    localErrors.value = [props.decimalPreventedMessage]
    if (props.localErrorTimeoutMs) {
      // Clear error after 3 seconds
      localErrorTimeout = setTimeout(() => {
        localErrors.value = []
      }, props.localErrorTimeoutMs)
    }
  }
}
</script>
